import React, { useContext, useEffect, useMemo, useState } from 'react';
import { asHTML } from '@prismicio/helpers';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { EXTERNAL_LINK_SVG, TICK_SVG } from '@constants';
import { replaceText } from '@utils/handleTextReplacement';
import { getTranslatedHtml } from '@utils/html';
import Colors from './Colors';
import { TEXT_STYLES, TextVariant } from './design/Typography';

interface Props {
  text?: string;
  richText?: any;
  textAlign?: string;
  textColor?: string;
  padding?: string;
  readMore?: boolean;
  onReadMore?: () => void;
  maxLength?: number;
  linkTargetBlank?: boolean;
  fontSize?: string;
  fontSizeMobile?: string;
}

const Text: React.FC<Props> = ({
  text,
  richText,
  textAlign = 'left',
  textColor = 'inherit',
  padding = '0 30px',
  readMore = true,
  onReadMore,
  maxLength = 100,
  linkTargetBlank = false,
  fontSize = '16px',
  fontSizeMobile = '16px'
}) => {
  const {
    state: { country }
  } = useContext(Context);
  const location = useLocation();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(readMore);

  useEffect(() => {
    setIsExpanded(readMore);
  }, [readMore]);

  const filteredTextHTML = useMemo(() => {
    const textHTML = richText ? asHTML(richText) : text;

    return replaceText(textHTML ?? '', country, location);
  }, [richText, text]);

  const displayText = useMemo(
    () => (isExpanded ? filteredTextHTML : `${filteredTextHTML.replace(/<\/?[^>]+(>|$)/g, '').substring(0, maxLength)}...`),
    [filteredTextHTML, isExpanded]
  );

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
    if (onReadMore) onReadMore();
  };

  return (
    <TextSection
      className="text-section"
      $padding={padding}
      $textAlign={textAlign}
      $textColor={textColor}
      $fontSize={fontSize}
      $fontSizeMobile={fontSizeMobile}
    >
      {isExpanded ? (
        <div>{getTranslatedHtml(displayText, linkTargetBlank)}</div>
      ) : (
        <p>
          {getTranslatedHtml(displayText, linkTargetBlank)}
          {filteredTextHTML.length > maxLength ? <ReadMoreLink onClick={toggleReadMore}>{t('components.read-more-alt')}</ReadMoreLink> : null}
        </p>
      )}
    </TextSection>
  );
};

export default Text;

interface ITextSection {
  $textAlign: string;
  $textColor: string;
  $padding?: string;
  $fontSize: string;
  $fontSizeMobile: string;
}

const TextSection = styled.section<ITextSection>`
  font-family: 'BrownStd';
  color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;

  text-align: ${(props) => (props.$textAlign ? `${props.$textAlign}` : 'left')};
  color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};

  & > {
    text-align: ${(props) => (props.$textAlign ? `${props.$textAlign}` : 'left')};
  }

  @media (max-width: 767px) {
    padding: 0;
  }

  a {
    color: ${Colors.lilac};
  }

  p:empty + p > a,
  p:last-child > a {
    color: ${Colors.navyBlue};
    border-bottom: 2px solid ${Colors.navyBlue};
    padding-bottom: 2px;
    font-weight: 700;
    text-decoration: none !important;
    transition: all 0.2s ease-in-out;
    font-size: 14px;

    &:hover {
      border-color: ${Colors.lilac};
      color: ${Colors.lilac};
    }
  }

  p:empty + p:empty + p > a {
    opacity: 0.5;
    border-bottom: 0;
    font-weight: 400;
  }

  p {
    font-family: 'BrownStd';
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
    font-size: ${(props) => props.$fontSize || '16px'};
    font-style: normal;
    font-weight: 400;
    line-height: 1.5;
    letter-spacing: 0;
    text-align: inherit;

    @media (max-width: 767px) {
      font-size: ${(props) => props.$fontSizeMobile || '16px'};
    }
  }

  p.block-img {
    margin-bottom: 20px;
  }

  li {
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
    font-size: ${(props) => props.$fontSize || '16px'};
    line-height: 24px;

    @media (max-width: 767px) {
      font-size: ${(props) => props.$fontSizeMobile || '16px'};
    }
  }

  span.tick-list {
    display: block;
    position: relative;
    padding: 6px 0 6px 35px;
    font-size: 18px;

    &:before {
      content: '';
      background-image: url(${TICK_SVG});
      background-repeat: no-repeat;
      background-position: center;
      width: 19px;
      height: 16px;
      position: absolute;
      left: 0;
      top: 7px;
    }

    @media (max-width: 768px) {
      text-align: left;
    }
  }

  h1,
  span.heading-1 {
    font-family: 'Domaine';
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
    font-family: Domaine;
    font-size: 50px;
    font-weight: normal;
    line-height: 120%;
    letter-spacing: -0.01em;
    text-align: center;

    @media (max-width: 1024px) {
      font-size: 34px;
      line-height: 150%;
    }
  }

  h2,
  span.heading-2 {
    font-family: 'Domaine';
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
    font-weight: normal;
    font-size: 40px;
    line-height: 120%;
    margin-bottom: 10px;

    @media (max-width: 1024px) {
      font-size: 24px;
    }
  }

  h3,
  span.heading-3 {
    font-family: 'Domaine';
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
    font-family: Domaine;
    font-size: 30px;
    font-style: normal;
    font-weight: normal;
    line-height: 120%;
    letter-spacing: -0.02em;

    @media (max-width: 1024px) {
      font-size: 24px;
      line-height: 150%;
    }
  }

  h4,
  span.heading-4 {
    font-family: 'Domaine';
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
    font-weight: normal;
    font-size: 18px;
    line-height: 140%;

    @media (max-width: 1024px) {
      font-size: 16px;
      line-height: 150%;
    }
  }

  h5,
  span.heading-5 {
    font-family: 'BrownStd';
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
    font-weight: bold;
    font-size: 15px;
    line-height: 140%;
    text-transform: uppercase;
    letter-spacing: 1.5px;

    @media (max-width: 1024px) {
      font-size: 14px;
      line-height: 150%;
    }
  }

  h6,
  span.heading-6 {
    font-family: 'BrownStd';
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : 'inherit')};
    font-style: normal;
    font-weight: bold;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 1.5px;
    text-transform: uppercase;

    @media (max-width: 1024px) {
      font-size: 12px;
    }
  }

  p:empty {
    height: 10px;
  }

  span.study-link {
    font-family: BrownStd;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;

    a {
      color: ${Colors.lilac};

      :hover {
        text-decoration: underline;
      }

      &:after {
        content: '';
        background-image: url(${EXTERNAL_LINK_SVG});
        background-repeat: no-repeat;
        background-position: center;
        width: 15px;
        height: 15px;
        display: inline-block;
        margin-left: 5px;
        vertical-align: -2px;
      }
    }
  }

  span.pink {
    color: ${Colors.lilac};
  }

  span.Heading1 {
    ${TEXT_STYLES[TextVariant.Heading1]}
  }

  span.Heading2 {
    ${TEXT_STYLES[TextVariant.Heading2]}
  }

  span.Heading3 {
    ${TEXT_STYLES[TextVariant.Heading3]}
  }

  span.Heading4 {
    ${TEXT_STYLES[TextVariant.Heading4]}
  }

  span.Heading5 {
    ${TEXT_STYLES[TextVariant.Heading5]}
  }

  span.Heading6 {
    ${TEXT_STYLES[TextVariant.Heading6]}
  }

  span.Heading7 {
    ${TEXT_STYLES[TextVariant.Heading7]}
  }

  span.Heading8 {
    ${TEXT_STYLES[TextVariant.Heading8]}
  }

  span.Heading9 {
    ${TEXT_STYLES[TextVariant.Heading9]}
  }

  span.Heading10 {
    ${TEXT_STYLES[TextVariant.Heading10]}
  }

  span.UpperText1 {
    ${TEXT_STYLES[TextVariant.UpperText1]}
  }

  span.UpperText2 {
    ${TEXT_STYLES[TextVariant.UpperText2]}
  }

  span.UpperText3 {
    ${TEXT_STYLES[TextVariant.UpperText3]}
  }

  span.UpperText4 {
    ${TEXT_STYLES[TextVariant.UpperText4]}
  }

  span.UpperText5 {
    ${TEXT_STYLES[TextVariant.UpperText5]}
  }

  span.BodyRegularSM {
    ${TEXT_STYLES[TextVariant.BodyRegularSM]}
  }

  span.BodyRegularMD {
    ${TEXT_STYLES[TextVariant.BodyRegularMD]}
  }

  span.BodyRegularLG {
    ${TEXT_STYLES[TextVariant.BodyRegularLG]}
  }

  span.BodyBoldSM {
    ${TEXT_STYLES[TextVariant.BodyBoldSM]}
  }

  span.BodyBoldMD {
    ${TEXT_STYLES[TextVariant.BodyBoldMD]}
  }

  span.BodyBoldLG {
    ${TEXT_STYLES[TextVariant.BodyBoldLG]}
  }

  span.mobile-Heading1 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading1]}
    }
  }

  span.mobile-Heading2 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading2]}
    }
  }

  span.mobile-Heading3 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading3]}
    }
  }

  span.mobile-Heading4 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading4]}
    }
  }

  span.mobile-Heading5 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading5]}
    }
  }

  span.mobile-Heading6 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading6]}
    }
  }

  span.mobile-Heading7 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading7]}
    }
  }

  span.mobile-Heading8 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading8]}
    }
  }

  span.mobile-Heading9 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading9]}
    }
  }

  span.mobile-Heading10 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.Heading10]}
    }
  }

  span.mobile-UpperText1 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.UpperText1]}
    }
  }

  span.mobile-UpperText2 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.UpperText2]}
    }
  }

  span.mobile-UpperText3 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.UpperText3]}
    }
  }

  span.mobile-UpperText4 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.UpperText4]}
    }
  }

  span.mobile-UpperText5 {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.UpperText5]}
    }
  }

  span.mobile-BodyRegularSM {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.BodyRegularSM]}
    }
  }

  span.mobile-BodyRegularMD {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.BodyRegularMD]}
    }
  }

  span.mobile-BodyRegularLG {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.BodyRegularLG]}
    }
  }

  span.mobile-BodyBoldSM {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.BodyBoldSM]}
    }
  }

  span.mobile-BodyBoldMD {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.BodyBoldMD]}
    }
  }

  span.mobile-BodyBoldLG {
    @media (max-width: 768px) {
      ${TEXT_STYLES[TextVariant.BodyBoldLG]}
    }
  }

  span.nowrap {
    white-space: nowrap;
  }
`;

const ReadMoreLink = styled.a`
  cursor: pointer;
  font-weight: normal !important;
  margin-left: 5px;
  padding-bottom: 0px !important;
  border-bottom-width: 1px !important;
`;
